import * as THREE from 'three';
import { PLAYER } from '../utils/constants.js';
import { events, EVT } from '../utils/events.js';
import { clamp } from '../utils/math.js';

/**
 * Outer Wilds-inspired first-person controller for walking on spherical planets.
 *
 * Key design:
 *  - Acceleration-based ground movement with velocity & friction (no instant teleport)
 *  - Jetpack with fuel (hold Space while airborne)
 *  - Shift boost dash (lateral burst in air)
 *  - Multi-sample terrain averaging for smooth ground following
 *  - Camera smoothly interpolates to physics position
 *  - All physics in world space; local frame only for input decomposition
 */
export class PlayerController {
  constructor(camera, scene) {
    this.camera = camera;
    this.scene = scene;
    this.enabled = false;

    // ── Physics state ──
    this.position = new THREE.Vector3(0, PLAYER.HEIGHT, 5);
    this.velocity = new THREE.Vector3();
    this.onGround = true;
    this._walkTimer = 0;

    // ── Camera smoothing ──
    this._smoothCamPos = new THREE.Vector3();
    this._smoothUp = new THREE.Vector3(0, 1, 0);
    this._camInitialized = false;

    // ── Look ──
    this.yaw = 0;
    this.pitch = 0;
    this.mouseDelta = { x: 0, y: 0 };

    // ── Local frame (rebuilt each frame from gravity) ──
    this._localUp = new THREE.Vector3(0, 1, 0);
    this._localForward = new THREE.Vector3(0, 0, -1);
    this._localRight = new THREE.Vector3(1, 0, 0);

    // ── Jetpack ──
    this.jetpackFuel = PLAYER.JETPACK_FUEL_MAX;
    this.jetpackActive = false;

    // ── Boost dash ──
    this.boostCooldown = 0;

    // ── Jump ──
    this._coyoteTimer = 0;   // allows jump shortly after leaving ground
    this._jumpBuffer = 0;    // allows pressing jump slightly before landing
    this._jumpLockout = 0;   // blocks ground snap briefly after jump

    // ── Interaction ──
    this.raycaster = new THREE.Raycaster();
    this.raycaster.far = 15;
    this.interactionTarget = null;
    this.interactionType = null;

    // ── Planet reference ──
    this.planetCenter = new THREE.Vector3(0, 0, 0);
    this.planetRadius = 100;
    this.surfaceHeightResolver = null;
    this._smoothedSurfaceRadius = null;
    this.gravityDir = new THREE.Vector3(0, -1, 0);
  }

  // ─── Public API ─────────────────────────────────

  enable() {
    this.enabled = true;
    this.velocity.set(0, 0, 0);
    this._camInitialized = false;
  }

  disable() {
    if (this.jetpackActive) {
      events.emit(EVT.PLAYER_JETPACK_STOP);
    }
    this.jetpackActive = false;
    this._walkTimer = 0;
    this.enabled = false;
  }

  setPlanet(center, radius) {
    this.planetCenter = center.clone();
    this.planetRadius = radius;
    this._smoothedSurfaceRadius = null;
  }

  setSurfaceHeightResolver(resolver) {
    this.surfaceHeightResolver = resolver || null;
    this._smoothedSurfaceRadius = null;
  }

  placeOnSurface(lat = 0, lon = 0) {
    const dir = new THREE.Vector3(
      Math.cos(lat) * Math.cos(lon),
      Math.sin(lat),
      Math.cos(lat) * Math.sin(lon)
    ).normalize();

    const surfR = this._getRawSurfaceRadius(dir);
    this.position.copy(this.planetCenter).addScaledVector(dir, surfR + PLAYER.HEIGHT);
    this.velocity.set(0, 0, 0);
    this.gravityDir.copy(dir).negate();
    this.onGround = true;
    this.yaw = 0;
    this.pitch = 0;
    this.jetpackFuel = PLAYER.JETPACK_FUEL_MAX;
    this._camInitialized = false;
  }

  getFacingDirection() {
    const localUp = this._localUp.clone();
    const qYaw = new THREE.Quaternion().setFromAxisAngle(localUp, this.yaw);
    const forward = this._localForward.clone().applyQuaternion(qYaw).normalize();
    const radialDir = this.position.clone().sub(this.planetCenter).normalize();
    const tangentForward = forward.sub(radialDir.multiplyScalar(forward.dot(radialDir)));
    return tangentForward.lengthSq() > 1e-6 ? tangentForward.normalize() : new THREE.Vector3(1, 0, 0);
  }

  handleMouseMove(dx, dy) {
    if (!this.enabled) return;
    this.mouseDelta.x += dx;
    this.mouseDelta.y += dy;
  }

  // ─── Terrain height ──────────────────────────────

  _getRawSurfaceRadius(normalizedDir) {
    if (typeof this.surfaceHeightResolver === 'function') {
      return this.surfaceHeightResolver(normalizedDir);
    }
    return this.planetRadius;
  }

  /**
   * Multi-sample terrain averaging: sample the height at several points
   * around the foot position to get a smooth effective ground height.
   */
  _getAveragedSurfaceRadius(radialDir) {
    const raw = this._getRawSurfaceRadius(radialDir);

    if (PLAYER.TERRAIN_SAMPLES <= 1 || !this.surfaceHeightResolver) {
      return raw;
    }

    // Build a tangent basis for sampling
    let tangent = new THREE.Vector3(0, 1, 0);
    if (Math.abs(radialDir.dot(tangent)) > 0.99) tangent.set(1, 0, 0);
    const t1 = new THREE.Vector3().crossVectors(radialDir, tangent).normalize();
    const t2 = new THREE.Vector3().crossVectors(radialDir, t1).normalize();

    let sum = raw;
    const sampleR = PLAYER.TERRAIN_SAMPLE_RADIUS / this.planetRadius;
    const n = PLAYER.TERRAIN_SAMPLES - 1;
    for (let i = 0; i < n; i++) {
      const angle = (i / n) * Math.PI * 2;
      const offset = t1.clone().multiplyScalar(Math.cos(angle) * sampleR)
        .addScaledVector(t2, Math.sin(angle) * sampleR);
      const sampleDir = radialDir.clone().add(offset).normalize();
      sum += this._getRawSurfaceRadius(sampleDir);
    }

    return sum / PLAYER.TERRAIN_SAMPLES;
  }

  /**
   * Smooth the terrain radius over time so small bumps don't jolt the player.
   */
  _getSmoothedSurfaceRadius(radialDir, dt) {
    const target = this._getAveragedSurfaceRadius(radialDir);

    if (this._smoothedSurfaceRadius == null) {
      this._smoothedSurfaceRadius = target;
      return target;
    }

    const speed = PLAYER.TERRAIN_SMOOTH_SPEED;
    const alpha = 1 - Math.exp(-speed * dt);
    this._smoothedSurfaceRadius += (target - this._smoothedSurfaceRadius) * alpha;
    return this._smoothedSurfaceRadius;
  }

  // ─── Local frame ──────────────────────────────────

  _buildLocalFrame() {
    this._localUp.copy(this.gravityDir).negate();

    const proj = this._localForward.clone().sub(
      this._localUp.clone().multiplyScalar(this._localForward.dot(this._localUp))
    );

    if (proj.lengthSq() < 1e-6) {
      const camFwd = new THREE.Vector3(0, 0, -1).applyQuaternion(this.camera.quaternion);
      proj.copy(camFwd).sub(this._localUp.clone().multiplyScalar(camFwd.dot(this._localUp)));
    }
    if (proj.lengthSq() < 1e-6) {
      const ref = Math.abs(this._localUp.y) > 0.99
        ? new THREE.Vector3(1, 0, 0) : new THREE.Vector3(0, 1, 0);
      proj.crossVectors(this._localUp, ref);
    }

    this._localForward.copy(proj.normalize());
    this._localRight.crossVectors(this._localForward, this._localUp).normalize();
    this._localForward.crossVectors(this._localUp, this._localRight).normalize();
  }

  // ─── Main update ──────────────────────────────────

  update(dt, input) {
    if (!this.enabled) return;

    const previousOnGround = this.onGround;
    const previousJetpackActive = this.jetpackActive;

    // Timers
    this._coyoteTimer = Math.max(0, this._coyoteTimer - dt);
    this._jumpBuffer = Math.max(0, this._jumpBuffer - dt);
    this._jumpLockout = Math.max(0, this._jumpLockout - dt);
    this.boostCooldown = Math.max(0, this.boostCooldown - dt);

    // ── Build local frame ──
    this._buildLocalFrame();
    const localUp = this._localUp;

    // ── Mouse look ──
    const lookSpeed = input.isMobile() ? PLAYER.LOOK_SPEED_TOUCH : PLAYER.LOOK_SPEED_MOUSE;
    this.yaw -= this.mouseDelta.x * lookSpeed;
    this.pitch -= this.mouseDelta.y * lookSpeed;
    this.pitch = clamp(this.pitch, -Math.PI / 2.2, Math.PI / 2.2);
    this.mouseDelta.x = 0;
    this.mouseDelta.y = 0;

    // Yawed frame
    const qYaw = new THREE.Quaternion().setFromAxisAngle(localUp, this.yaw);
    const yawedFwd = this._localForward.clone().applyQuaternion(qYaw).normalize();
    const yawedRight = this._localRight.clone().applyQuaternion(qYaw).normalize();

    // View direction (yaw + pitch)
    const qPitch = new THREE.Quaternion().setFromAxisAngle(yawedRight, this.pitch);
    const lookDir = yawedFwd.clone().applyQuaternion(qPitch).normalize();

    // ── Input direction (tangent plane) ──
    const wishDir = new THREE.Vector3();
    if (input.keys.has('KeyW') || input.keys.has('ArrowUp')) wishDir.add(yawedFwd);
    if (input.keys.has('KeyS') || input.keys.has('ArrowDown')) wishDir.sub(yawedFwd);
    if (input.keys.has('KeyD') || input.keys.has('ArrowRight')) wishDir.add(yawedRight);
    if (input.keys.has('KeyA') || input.keys.has('ArrowLeft')) wishDir.sub(yawedRight);
    const hasInput = wishDir.lengthSq() > 1e-6;
    if (hasInput) wishDir.normalize();

    const isSprint = input.keys.has('ShiftLeft') || input.keys.has('ShiftRight') || input.keys.has('Action');
    const wantJump = input.keys.has('Space');

    // ── Decompose velocity into radial / tangential ──
    const radialDir = this.position.clone().sub(this.planetCenter);
    const dist = radialDir.length();
    if (dist > 0.01) radialDir.divideScalar(dist); else radialDir.copy(localUp);
    const radialSpeed = this.velocity.dot(radialDir);
    const tangentialVel = this.velocity.clone().addScaledVector(radialDir, -radialSpeed);

    // ── Gravity — only when airborne ──
    // When grounded the normal force cancels gravity; applying it causes
    // the player to oscillate through the surface every frame.
    if (!this.onGround) {
      this.velocity.addScaledVector(this.gravityDir, PLAYER.GRAVITY * dt);
    } else {
      // On ground: kill any radial velocity so we don't sink / float
      this._killRadialVelocity(radialDir);
    }

    // ── Ground movement ──
    if (this.onGround) {
      const targetSpeed = isSprint ? PLAYER.RUN_SPEED : PLAYER.WALK_SPEED;

      if (hasInput) {
        const wishVel = wishDir.clone().multiplyScalar(targetSpeed);
        const diff = wishVel.clone().sub(tangentialVel);
        const step = Math.min(PLAYER.GROUND_ACCEL * dt, diff.length());
        if (step > 1e-6) this.velocity.addScaledVector(diff.normalize(), step);
      } else {
        // Friction
        const factor = Math.exp(-PLAYER.GROUND_FRICTION * dt);
        this.velocity.add(tangentialVel.clone().multiplyScalar(factor - 1));
      }

      // Recharge jetpack
      this.jetpackFuel = Math.min(PLAYER.JETPACK_FUEL_MAX, this.jetpackFuel + PLAYER.JETPACK_RECHARGE * dt);

      // Coyote time
      this._coyoteTimer = 0.12;

      if (hasInput && this.velocity.length() > 1.5) {
        this._walkTimer -= dt;
        if (this._walkTimer <= 0) {
          events.emit(EVT.PLAYER_FOOTSTEP);
          this._walkTimer = isSprint ? 0.25 : 0.35;
        }
      } else {
        this._walkTimer = 0;
      }
    } else {
      // Air control (reduced)
      if (hasInput) {
        this.velocity.addScaledVector(wishDir, PLAYER.AIR_ACCEL * dt);
      }
      // Light air friction on tangential
      const factor = Math.exp(-PLAYER.AIR_FRICTION * dt);
      this.velocity.add(tangentialVel.clone().multiplyScalar(factor - 1));
    }

    // ── Jump ──
    // Buffer: pressing Space slightly before landing still fires.
    if (wantJump) {
      this._jumpBuffer = 0.1;
    }

    let didJump = false;
    const canJump = this.onGround || this._coyoteTimer > 0;
    if (this._jumpBuffer > 0 && canJump && this._jumpLockout <= 0) {
      // Zero radial velocity then impulse outward
      this._killRadialVelocity(radialDir);
      this.velocity.addScaledVector(localUp, PLAYER.JUMP_IMPULSE);
      this.onGround = false;
      this._coyoteTimer = 0;
      this._jumpBuffer = 0;
      this._jumpLockout = PLAYER.JUMP_LOCKOUT;
      events.emit(EVT.PLAYER_JUMP);
      didJump = true;
    }

    // ── Jetpack (hold Space while airborne) ──
    this.jetpackActive = false;
    if (!this.onGround && !didJump && wantJump && this.jetpackFuel > 0) {
      this.velocity.addScaledVector(localUp, PLAYER.JETPACK_THRUST * dt);
      this.jetpackFuel = Math.max(0, this.jetpackFuel - PLAYER.JETPACK_DRAIN * dt);
      this.jetpackActive = true;
    }

    if (this.jetpackActive && !previousJetpackActive) {
      events.emit(EVT.PLAYER_JETPACK_START);
    } else if (!this.jetpackActive && previousJetpackActive) {
      events.emit(EVT.PLAYER_JETPACK_STOP);
    }

    // ── Air boost dash (Shift while airborne + moving, one-shot cooldown) ──
    if (!this.onGround && isSprint && this.boostCooldown <= 0 && hasInput) {
      this.velocity.addScaledVector(wishDir, PLAYER.BOOST_IMPULSE);
      this.boostCooldown = PLAYER.BOOST_COOLDOWN;
      events.emit(EVT.PLAYER_BOOST);
    }

    // ── Integrate position ──
    this.position.addScaledVector(this.velocity, dt);

    // ── NaN / Infinity guard ──
    if (!isFinite(this.position.x) || !isFinite(this.position.y) || !isFinite(this.position.z)) {
      const safeDir = this.gravityDir.clone().negate();
      const safeR = this._getRawSurfaceRadius(safeDir);
      this.position.copy(this.planetCenter).addScaledVector(safeDir, safeR + PLAYER.HEIGHT);
      this.velocity.set(0, 0, 0);
    }

    // ── Ground collision ──
    const groundVec = this.position.clone().sub(this.planetCenter);
    const distToCenter = groundVec.length();
    const groundNorm = distToCenter > 0.01
      ? groundVec.divideScalar(distToCenter)
      : localUp.clone();

    const rawR = this._getRawSurfaceRadius(groundNorm);
    const smoothR = this._getSmoothedSurfaceRadius(groundNorm, dt);
    const hardFloor = rawR + PLAYER.HEIGHT;
    const softFloor = smoothR + PLAYER.HEIGHT;

    const inJumpLockout = this._jumpLockout > 0;

    if (distToCenter <= hardFloor) {
      // Hard resolve — never go below real terrain, even during jump lockout
      this.position.copy(this.planetCenter).addScaledVector(groundNorm, hardFloor);
      this._killRadialVelocity(groundNorm);
      // Only re-ground if not in jump lockout (so jumps aren't instantly canceled)
      if (!inJumpLockout) {
        this.onGround = true;
      }
    } else if (!inJumpLockout && distToCenter <= softFloor + PLAYER.GROUND_SNAP) {
      // Soft snap — disabled during jump lockout so jump can escape snap zone
      this.position.copy(this.planetCenter).addScaledVector(groundNorm, softFloor);
      this._killRadialVelocity(groundNorm);
      this.onGround = true;
    } else {
      this.onGround = false;
    }

    if (this.onGround && !previousOnGround) {
      events.emit(EVT.PLAYER_LAND);
    }

    // ── Update gravity direction ──
    this.gravityDir.copy(this.position).sub(this.planetCenter).normalize().negate();

    // ── Clamp max speed ──
    const maxSpeed = 80;
    if (this.velocity.lengthSq() > maxSpeed * maxSpeed) {
      this.velocity.normalize().multiplyScalar(maxSpeed);
    }

    // ── Camera ──
    this._updateCamera(dt, lookDir);

    // ── Interaction ──
    this._checkInteractions();
  }

  // ─── Helpers ──────────────────────────────────────

  _killRadialVelocity(radialDir) {
    const rv = this.velocity.dot(radialDir);
    if (Math.abs(rv) > 1e-6) {
      this.velocity.addScaledVector(radialDir, -rv);
    }
  }

  _updateCamera(dt, lookDir) {
    const target = this.position;

    if (!this._camInitialized) {
      this._smoothCamPos.copy(target);
      this._smoothUp.copy(this._localUp);
      this._camInitialized = true;
    } else {
      // Position: high alpha for snappy follow, slight smoothing removes jitter
      const posAlpha = 1 - Math.exp(-35 * dt);
      this._smoothCamPos.lerp(target, posAlpha);

      // Up vector: slower interpolation for silky-smooth orientation on curved terrain
      const upAlpha = 1 - Math.exp(-16 * dt);
      this._smoothUp.lerp(this._localUp, upAlpha).normalize();
    }

    this.camera.position.copy(this._smoothCamPos);
    this.camera.up.copy(this._smoothUp);
    this.camera.lookAt(this._smoothCamPos.clone().add(lookDir));
  }

  _checkInteractions() {
    this.raycaster.set(
      this.camera.position,
      new THREE.Vector3(0, 0, -1).applyQuaternion(this.camera.quaternion)
    );
    const intersects = this.raycaster.intersectObjects(this.scene.children, true);

    this.interactionTarget = null;
    this.interactionType = null;

    for (const hit of intersects) {
      let current = hit.object;
      while (current) {
        const n = current.name;
        if (n === 'pilot-seat') { this.interactionTarget = current; this.interactionType = 'cockpit'; return; }
        if (n === 'console-screen' || n === 'nav-console-base' || n === 'holo-beam') {
          this.interactionTarget = current; this.interactionType = 'console'; return;
        }
        if (n === 'radio-box') { this.interactionTarget = current; this.interactionType = 'radio'; return; }
        if (n === 'ship-door') { this.interactionTarget = current; this.interactionType = 'door'; return; }
        if (n === 'spaceship') { this.interactionTarget = current; this.interactionType = 'ship'; return; }
        if (n && n.startsWith('story-')) { this.interactionTarget = current; this.interactionType = 'story'; return; }
        if (n && n.startsWith('creature-')) { this.interactionTarget = current; this.interactionType = 'creature'; return; }
        if (n && n.startsWith('anomaly-')) { this.interactionTarget = current; this.interactionType = 'anomaly'; return; }
        current = current.parent;
      }
    }
  }
}
